import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQueryWithReauth';
import { BaseEntity } from '../Types/baseTypes';
import { TagTypes } from '../Types/tagTypes';

interface CreateGenericApiSliceProps<
  T extends BaseEntity,
  TCreate = Omit<T, 'id'>,
  TUpdate = Partial<T>,
  TCreateResponse = T,
  TUpdateResponse = T
> {
  reducerPath: string;
  tag: TagTypes;
  endpoint: string;
  // Phantom property to bind generic parameters and avoid TS unused type warnings; not required at runtime
  _types?: {
    create: TCreate;
    update: TUpdate;
    createResponse: TCreateResponse;
    updateResponse: TUpdateResponse;
  };
}

export function createGenericApiSlice<
  T extends BaseEntity,
  TCreate = Omit<T, 'id'>,
  TUpdate = Partial<T>,
  TCreateResponse = T,
  TUpdateResponse = T
>({
  reducerPath,
  tag,
  endpoint,
}: CreateGenericApiSliceProps<T, TCreate, TUpdate, TCreateResponse, TUpdateResponse>) {
  return createApi({
    reducerPath,
    baseQuery: baseQueryWithReauth, // Use enhanced baseQuery with automatic refresh
    tagTypes: [tag],
    endpoints: (builder) => ({
      getAll: builder.query<T[], void>({
        query: () => `/${endpoint}`,
        providesTags: (result) =>
          result
            ? [
                ...result.map((item) => ({ type: tag, id: item.id })),
                { type: tag, id: TagTypes.List },
              ]
            : [{ type: tag, id: TagTypes.List }],
      }),
      getById: builder.query<T, number>({
        query: (id) => `/${endpoint}/${id}`,
        providesTags: (result, error, id) => [{ type: tag, id }],
      }),
      create: builder.mutation<TCreateResponse, TCreate>({
        query: (data) => ({
          url: `/${endpoint}`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: [
          { type: tag, id: TagTypes.List },
          // Invalidate CashBalance when creating cash documents
          ...(tag === TagTypes.CashDocument ? [TagTypes.CashBalance] : []),
          // Invalidate Reservation when creating/updating cash documents (to refresh payment status)
          ...(tag === TagTypes.CashDocument
            ? [{ type: TagTypes.Reservation, id: TagTypes.List }]
            : []),
        ],
      }),
      update: builder.mutation<TUpdateResponse, { id: number; data: TUpdate }>({
        query: ({ id, data }) => ({
          url: `/${endpoint}/${id}`,
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: (result, error, { id, data }) => {
          const candidateOfferId = (data as { offerId?: unknown }).offerId;
          const bookingOfferTag =
            tag === TagTypes.Booking &&
            typeof candidateOfferId === 'number' &&
            Number.isInteger(candidateOfferId) &&
            candidateOfferId > 0
              ? [{ type: tag, id: `offer-${candidateOfferId}` }]
              : [];

          return [
            { type: tag, id },
            { type: tag, id: TagTypes.List },
            ...bookingOfferTag,
            // Invalidate CashBalance when updating cash documents
            ...(tag === TagTypes.CashDocument ? [TagTypes.CashBalance] : []),
            // Invalidate Reservation when creating/updating cash documents (to refresh payment status)
            ...(tag === TagTypes.CashDocument
              ? [{ type: TagTypes.Reservation, id: TagTypes.List }]
              : []),
          ];
        },
      }),
      delete: builder.mutation<void, number>({
        query: (id) => ({
          url: `/${endpoint}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, id) => [
          { type: tag, id },
          { type: tag, id: TagTypes.List },
          // Invalidate CashBalance when deleting cash documents
          ...(tag === TagTypes.CashDocument ? [TagTypes.CashBalance] : []),
          // Invalidate Reservation when creating/updating cash documents (to refresh payment status)
          ...(tag === TagTypes.CashDocument
            ? [{ type: TagTypes.Reservation, id: TagTypes.List }]
            : []),
        ],
      }),
    }),
  });
}
